import { Cog8ToothIcon } from "@heroicons/react/24/outline"
import { Card, Dialog } from "~/ui-rtk/components/ui/common"
import { Button, ChipsInput, Input } from "~/ui-rtk/components/ui/controls"
import { H3 } from "~/ui-rtk/components/ui/typography"
import useConnect, { TMindShareSettingsFormValues } from "./connect"
import { useRef, useState } from "react"
import { Field, FieldProps, Form, Formik, FormikProps } from "formik"
import KeywordsChangesDialog from "~/ui-rtk/components/ui/common/KeywordsChangesDialog/KeywordsChangesDialog"
import useDemoMode from "~/ui-rtk/hooks/demo-mode"
import { debounce } from "lodash"
import { ErrorBlock } from "~/ui-rtk/utils/form"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

export type TDialogProps = {
  onClose: () => void
}

export default function MindShareSettingsDialog({ onClose }: TDialogProps) {
  const {
    companyName,
    initialValues,
    validationSchema,
    updateKeywords,
    dontShowDialogAgain,
    setDontShowDialogAgain,
  } = useConnect()
  const [showInfoDialog, setShowInfoDialog] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [uiDontShowDialog, setUiDontShowDialog] = useState(false)
  const formRef = useRef<FormikProps<TMindShareSettingsFormValues> | null>(null)

  const handleUpdateKeywords = async (values: TMindShareSettingsFormValues) => {
    setIsLoading(true)
    await updateKeywords(values)
    setIsLoading(false)
    setShowInfoDialog(true)
    setUiDontShowDialog(dontShowDialogAgain)
  }

  const handleCloseInfoDialog = () => {
    setShowInfoDialog(false)
  }

  const handleSubmit = async () => {
    await formRef.current?.validateForm()
    if (formRef.current?.isValid) {
      handleUpdateKeywords(formRef.current?.values).catch(() => null)
    }
  }

  const handleSubmitDebounced = debounce(handleSubmit, 200)

  const { isEnabled: isDemoEnabled } = useDemoMode()

  const infoDialogContent =
    !uiDontShowDialog && showInfoDialog ? (
      <KeywordsChangesDialog
        onClose={handleCloseInfoDialog}
        dontShowDialogAgain={dontShowDialogAgain}
        setDontShowDialogAgain={setDontShowDialogAgain}
      />
    ) : null

  return (
    <Dialog
      width={750}
      isOpen={true}
      handleClose={onClose}
      closeOnClickOutside={true}
    >
      <Card rounded className={`p-5`}>
        <div className="flex justify-start items-center gap-2">
          <div>
            <Cog8ToothIcon className="w-5 h-5" />
          </div>
          <div className="col-span-7">
            <H3 className="text-start">Share of Search settings</H3>
          </div>
        </div>
        <div className={cn("mt-2 text-start", isDemoEnabled && "blur-md")}>
          <Formik
            initialValues={initialValues}
            onSubmit={handleUpdateKeywords}
            validationSchema={validationSchema}
            enableReinitialize
            innerRef={formRef}
            validateOnBlur={false}
            validateOnChange={false}
          >
            {({ errors, handleChange /* ,  touched */ }) => (
              <Form className="py-2 space-y-2">
                <div className="relative col-span-2">
                  <label className="leading-8" htmlFor="googleCategories">
                    Primary Brand Google Keyword:
                    <br />
                    <Field
                      id="googleSearchAlias"
                      name="googleSearchAlias"
                      as={Input}
                      type="text"
                      fullWidth
                      placeholder={companyName}
                      onChange={(ev: React.ChangeEvent<string>) => {
                        handleChange(ev)
                      }}
                    />
                    <ErrorBlock error={errors.googleSearchAlias} />
                  </label>
                </div>
                <div className="relative col-span-2">
                  <label className="leading-8" htmlFor="googleCategories">
                    Competitor Google Keywords *:
                    <br />
                    <Field id="googleCompetitors" name="googleCompetitors">
                      {({
                        field, // { name, value, onChange, onBlur }
                        form: { setFieldValue }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
                        // meta,
                      }: FieldProps) => (
                        <ChipsInput
                          {...field}
                          onChange={async value => {
                            await setFieldValue("googleCompetitors", value)
                          }}
                        />
                      )}
                    </Field>
                    <ErrorBlock error={errors.googleCompetitors} />
                  </label>
                </div>
                <div className="relative col-span-2">
                  <label className="leading-8" htmlFor="googleCategories">
                    Category Google Keywords *:
                    <br />
                    <Field id="googleCategories" name="googleCategories">
                      {({
                        field, // { name, value, onChange, onBlur }
                        form: { setFieldValue }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
                        // meta,
                      }: FieldProps) => (
                        <ChipsInput
                          {...field}
                          onChange={async value => {
                            await setFieldValue("googleCategories", value)
                          }}
                        />
                      )}
                    </Field>
                    <ErrorBlock error={errors.googleCategories} />
                  </label>
                </div>
              </Form>
            )}
          </Formik>
          {infoDialogContent}
        </div>
        <div className="mt-4 text-center flex justify-between gap-2">
          <div className="w-full">
            <Button
              variant={{ variant: "solid", color: "blue" }}
              className={`btn flex align-center btn-block`}
              size="lg"
              isLoading={isLoading}
              onClick={handleSubmitDebounced}
            >
              Save
            </Button>
          </div>
          <div className="w-full">
            <Button
              variant={{ variant: "solid", color: "blue" }}
              className={`btn flex align-center btn-block`}
              size="lg"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </div>
      </Card>
    </Dialog>
  )
}
